/**
 * Microphone capture.
 *
 * Captures the mic via `getUserMedia` + an `AudioWorklet`, resamples to a fixed
 * target rate, and emits mono 16-bit PCM frames plus a smoothed input level for
 * a visualizer. Provider-neutral: a WebSocket/PCM transport can feed these
 * frames upstream, while a WebRTC transport uses the raw `MediaStreamTrack`
 * directly but can still reuse the level meter.
 */

import { floatToPcm16, resampleLinear } from "./pcm.js";
import { ensureWorklet } from "./worklet-loader.js";
import { MIC_CAPTURE_PROCESSOR, MIC_CAPTURE_WORKLET } from "./worklet-sources.js";

export interface MicCaptureOptions {
  /** Target sample rate (Hz) for emitted PCM frames. Default `24000`. */
  targetSampleRate?: number;
  /** Chunk size (samples at the context rate) accumulated before a frame is emitted. Default `2048`. */
  chunkSamples?: number;
  /** Reuse an existing `AudioContext` instead of creating one. */
  audioContext?: AudioContext;
  /** `getUserMedia` audio constraints. Defaults enable echo cancellation, noise suppression, and AGC. */
  constraints?: MediaTrackConstraints;
  /** Smoothing factor (`0..1`) for the reported input level. Default `0.3`. */
  levelSmoothing?: number;
}

export interface MicCaptureEvents {
  /** A mono 16-bit PCM frame at `targetSampleRate`. */
  onFrame?(frame: Int16Array): void;
  /** Smoothed input level (`0..1`) for a visualizer. */
  onLevel?(level: number): void;
  onError?(err: Error): void;
}

/**
 * Mic → 16-bit PCM frame pipeline. Call {@link start} after a user gesture
 * (permission prompt), {@link setMuted} to gate the mic, and {@link stop} to
 * release the device.
 */
export class MicCapture {
  private readonly targetSampleRate: number;
  private readonly chunkSamples: number;
  private readonly constraints: MediaTrackConstraints;
  private readonly levelSmoothing: number;
  private readonly ownsContext: boolean;

  private ctx: AudioContext | undefined;
  private stream: MediaStream | undefined;
  private source: MediaStreamAudioSourceNode | undefined;
  private node: AudioWorkletNode | undefined;
  private starting = false;
  private muted = false;
  private level = 0;

  constructor(
    private readonly events: MicCaptureEvents = {},
    options: MicCaptureOptions = {},
  ) {
    this.targetSampleRate = options.targetSampleRate ?? 24000;
    this.chunkSamples = options.chunkSamples ?? 2048;
    this.levelSmoothing = options.levelSmoothing ?? 0.3;
    this.constraints = options.constraints ?? {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    this.ctx = options.audioContext;
    this.ownsContext = options.audioContext === undefined;
  }

  /** The live mic `MediaStream` once started (e.g. to add tracks to an `RTCPeerConnection`). */
  get mediaStream(): MediaStream | undefined {
    return this.stream;
  }

  /** Request the mic and start emitting frames. Rejects if permission is denied. */
  async start(): Promise<void> {
    // Synchronous guard: `this.node` isn't set until after the awaits below, so a
    // concurrent start() call would otherwise re-acquire the mic and orphan the
    // first MediaStream. Set the flag before the first await.
    if (this.node || this.starting) return;
    this.starting = true;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: this.constraints });
      const ctx = this.ctx ?? new AudioContext();
      this.ctx = ctx;
      if (ctx.state === "suspended") await ctx.resume();

      await ensureWorklet(ctx, MIC_CAPTURE_PROCESSOR, MIC_CAPTURE_WORKLET);

      this.source = ctx.createMediaStreamSource(this.stream);
      this.node = new AudioWorkletNode(ctx, MIC_CAPTURE_PROCESSOR, {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        processorOptions: { chunkSamples: this.chunkSamples },
      });
      this.node.port.onmessage = (e: MessageEvent) => this.onWorkletMessage(e);
      this.source.connect(this.node);
    } catch (err) {
      // If we already acquired the mic, release it (and the audio graph) before rethrowing.
      try {
        await this.stop();
      } catch {
        // Ignore cleanup failures; surface the original error.
      }
      const error = err instanceof Error ? err : new Error(String(err));
      this.events.onError?.(error);
      throw error;
    } finally {
      this.starting = false;
    }
  }

  private onWorkletMessage(e: MessageEvent): void {
    const data = e.data as { type: string; frame?: Float32Array; level?: number };
    if (data.type !== "frame" || !data.frame) return;

    const smoothed = this.level + this.levelSmoothing * ((data.level ?? 0) - this.level);
    this.level = this.muted ? 0 : smoothed;
    this.events.onLevel?.(this.level);

    if (this.muted) return;
    const ctxRate = this.ctx?.sampleRate ?? this.targetSampleRate;
    const resampled = resampleLinear(data.frame, ctxRate, this.targetSampleRate);
    this.events.onFrame?.(floatToPcm16(resampled));
  }

  /** Mute/unmute the mic. Muted = the track produces silence and no frames are emitted. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    for (const track of this.stream?.getAudioTracks() ?? []) {
      track.enabled = !muted;
    }
    if (muted) {
      this.level = 0;
      this.events.onLevel?.(0);
    }
  }

  /** Latest smoothed input level (`0..1`). */
  getInputLevel(): number {
    return this.level;
  }

  /** Stop capture, release the mic, and close the context if this instance created it. */
  async stop(): Promise<void> {
    this.source?.disconnect();
    this.node?.disconnect();
    if (this.node) this.node.port.onmessage = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    if (this.ownsContext && this.ctx) await this.ctx.close();
    this.source = undefined;
    this.node = undefined;
    this.stream = undefined;
    if (this.ownsContext) this.ctx = undefined;
    this.level = 0;
  }
}
